import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  IconButton,
  Tooltip,
  AppBar,
  Toolbar,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  CircularProgress,
  Backdrop,
} from '@mui/material';
import {
  Preview,
  Undo,
  Redo,
  Settings,
  Smartphone,
  Tablet,
  Computer,
  Close,
  Download,
  ArrowBack,
} from '@mui/icons-material';

// Импортируем наши модульные блоки
import BlockLibrary from './components/BlockLibrary';
import EmailCanvas from './components/EmailCanvas';
import PropertyPanel from './components/PropertyPanel';
import PreviewPanel from './components/PreviewPanel';
import HistoryManager from './utils/HistoryManager';
import EmailExporterImproved from './utils/EmailExporterImproved';
import { findBlockById, ensureUnsubscribeLast, normalizeHeroData } from './utils/blockUtils';
import { isAdminRole } from '../../utils/userRoles';

// Определяем размеры для разных режимов просмотра
const responsiveWidths = {
  desktop: '100%',
  tablet: 768,
  mobile: 375,
};

const MailsballEditorPro = ({ 
  onSave, 
  onClose, 
  initialTemplate = null 
}) => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Основные состояния
  const [blocks, setBlocks] = useState([]);
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [user, setUser] = useState(null);
  const [previewMode, setPreviewMode] = useState('desktop'); // desktop, tablet, mobile
  const [showPreview, setShowPreview] = useState(false);
  const [templateName, setTemplateName] = useState('Untitled Template');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Диалоги
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [showExitConfirmDialog, setShowExitConfirmDialog] = useState(false);
  
  // Настройки шаблона
  const [templateSettings, setTemplateSettings] = useState({
    width: 1200, // Увеличиваем базовую ширину
    backgroundColor: '#f5f5f5',
    padding: 20,
    fontFamily: 'Arial, sans-serif',
    fontSize: 14,
    blockSpacing: 5, // Добавляем отступ между блоками
  });

  // История изменений
  const historyManager = useRef(new HistoryManager());
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const normalizeBlocks = (rawBlocks) => {
    return ensureUnsubscribeLast(
      (rawBlocks || []).map((block) =>
        block.type === 'hero'
          ? { ...block, data: normalizeHeroData(block.data) }
          : block
      )
    );
  };

  const initHistory = (nextBlocks, nextSettings) => {
    historyManager.current.clear();
    historyManager.current.save({ blocks: nextBlocks, settings: nextSettings });
    setCanUndo(historyManager.current.canUndo());
    setCanRedo(historyManager.current.canRedo());
  };

  const showNotification = (message, severity = 'success') => {
    setNotification({ open: true, message, severity });
  };

  // Функция для создания блока Unsubscribe
  const createUnsubscribeBlock = () => ({
    id: `unsubscribe_${Date.now()}`,
    type: 'unsubscribe',
    data: {
      text: 'If you no longer wish to receive these emails, you can',
      buttonText: 'Unsubscribe',
      textColor: '#666666',
      buttonColor: '#007bff',
      buttonTextColor: '#ffffff',
      fontSize: 12,
      textAlign: 'center',
      backgroundColor: 'transparent',
      padding: 20,
      marginTop: 20,
      marginBottom: 20,
    },
    position: 9999,
    isReadOnly: !isAdminRole(user?.role),
  });

  const ensureUnsubscribeBlock = (currentBlocks) => {
    const blocksList = currentBlocks || [];
    if (blocksList.some((block) => block.type === 'unsubscribe')) {
      return normalizeBlocks(blocksList);
    }
    return normalizeBlocks([...blocksList, createUnsubscribeBlock()]);
  };

  const loadTemplate = (template) => {
    const templateBlocks = ensureUnsubscribeBlock(template.blocks || []);
    const nextSettings = template.settings || templateSettings;
    setBlocks(templateBlocks);
    setTemplateName(template.name || 'Untitled Template');
    setTemplateSettings(nextSettings);
    initHistory(templateBlocks, nextSettings);
    setHasUnsavedChanges(false);
  };

  const bootstrapNewTemplate = () => {
    const unsubscribeBlock = createUnsubscribeBlock();
    const nextBlocks = [unsubscribeBlock];
    setBlocks(nextBlocks);
    initHistory(nextBlocks, templateSettings);
    setHasUnsavedChanges(false);
  };
  useEffect(() => {
    // Создаем AbortController для отмены запросов при размонтировании
    const abortController = new AbortController();
    
    const fetchCurrentUserWithAbort = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const config = { 
          headers: { 'Authorization': `Bearer ${token}` },
          signal: abortController.signal
        };
        const response = await axios.get('/api/auth/me', config);
        
        // Проверяем, не был ли запрос отменен
        if (!abortController.signal.aborted) {
          setUser(response.data);
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error fetching user:', error);
        }
      }
    };
    
    const fetchTemplateWithAbort = async (id) => {
      if (!id) return;

      setIsLoading(true);
      try {
        const token = localStorage.getItem('token');
        const config = {
          headers: { Authorization: `Bearer ${token}` },
          signal: abortController.signal,
        };
        const response = await axios.get(`/api/templates/${id}`, config);

        if (abortController.signal.aborted) return;

        const template = response.data;
        if (!template) {
          throw new Error('Template not found');
        }

        loadTemplate(template);
        showNotification('Template loaded successfully');
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error loading template:', error);

          if (error.response?.status === 404) {
            showNotification('Template not found. Creating new template...', 'warning');
            setTemplateName('New Template');
            bootstrapNewTemplate();
            window.history.replaceState({}, '', '/templates/editor-pro');
          } else {
            showNotification('Error loading template', 'error');
            bootstrapNewTemplate();
          }
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    fetchCurrentUserWithAbort();

    const templateData = location.state?.templateData;

    if (templateData) {
      setTemplateName(templateData.name || 'Untitled Template');

      const hasValidBlocks =
        templateData.blocks &&
        Array.isArray(templateData.blocks) &&
        templateData.blocks.length > 0;

      if (hasValidBlocks) {
        loadTemplate({
          name: templateData.name,
          blocks: templateData.blocks,
          settings: templateData.settings,
        });
        showNotification('Template loaded successfully');
      } else if (templateData.id) {
        fetchTemplateWithAbort(templateData.id);
      } else {
        bootstrapNewTemplate();
      }
    } else if (templateId) {
      fetchTemplateWithAbort(templateId);
    } else if (initialTemplate) {
      loadTemplate(initialTemplate);
    } else {
      bootstrapNewTemplate();
    }

    return () => {
      abortController.abort();
    };
    // Intentional: mount/route init only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, location.state]);

  // Sync unsubscribe protection when user role becomes available
  useEffect(() => {
    if (!user) return;
    const readOnly = !isAdminRole(user.role);
    setBlocks((prev) =>
      prev.map((block) =>
        block.type === 'unsubscribe' && block.isReadOnly !== readOnly
          ? { ...block, isReadOnly: readOnly }
          : block
      )
    );
  }, [user]);

  // Обновление истории
  useEffect(() => {
    if (historyManager.current) {
      setCanUndo(historyManager.current.canUndo());
      setCanRedo(historyManager.current.canRedo());
    }
  }, [blocks, templateSettings]);

  // Обработчик предупреждения при закрытии браузера
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return 'You have unsaved changes. Are you sure you want to leave?';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const handleUpdateSettings = (newSettings) => {
    setTemplateSettings(newSettings);
    historyManager.current.save({ blocks, settings: newSettings });
    setHasUnsavedChanges(true);
  };

  const handleAddBlock = (blockType, blockData, insertPosition = null) => {
    // Запрещаем добавление дополнительных блоков Unsubscribe
    if (blockType === 'unsubscribe') {
      showNotification('Unsubscribe block already exists and cannot be duplicated', 'warning');
      return;
    }
    
    const newBlock = {
      id: Date.now().toString(),
      type: blockType,
      data: blockType === 'hero' ? normalizeHeroData(blockData) : blockData,
      position: 0,
    };
    
    let newBlocksRaw;
    if (insertPosition !== null && insertPosition < blocks.length) {
      newBlocksRaw = [...blocks];
      newBlocksRaw.splice(insertPosition, 0, newBlock);
    } else {
      const unsubscribeIndex = blocks.findIndex((block) => block.type === 'unsubscribe');
      if (unsubscribeIndex !== -1) {
        newBlocksRaw = [...blocks];
        newBlocksRaw.splice(unsubscribeIndex, 0, newBlock);
      } else {
        newBlocksRaw = [...blocks, newBlock];
      }
    }

    const newBlocks = ensureUnsubscribeLast(newBlocksRaw);

    setBlocks(newBlocks);
    historyManager.current.save({ blocks: newBlocks, settings: templateSettings });
    setHasUnsavedChanges(true);
  };

  // Рекурсивное обновление вложенных блоков по id
  const deepUpdate = (blk, targetId, update) => {
    if (!blk) return blk;
    if (blk.id === targetId) {
      const updatedData = typeof update === 'function'
        ? update(blk.data || {})
        : { ...(blk.data || {}), ...(update || {}) };
      return { ...blk, data: updatedData };
    }

    // Layout blocks – проходим содержимое рекурсивно
    if (blk.type === 'container') {
      const newContent = (blk.data?.content || []).map(child => deepUpdate(child, targetId, update));
      // Если ничего не изменилось, вернём прежний объект чтобы не создавать лишних ререндеров
      if (newContent.every((c, i) => c === (blk.data?.content || [])[i])) return blk;
      return { ...blk, data: { ...(blk.data || {}), content: newContent } };
    }

    if (blk.type === 'columns' || blk.type === 'columns-3') {
      let changed = false;
      const cloneIfChanged = arr => arr.map(child => {
        const updated = deepUpdate(child, targetId, update);
        if (updated !== child) changed = true;
        return updated;
      });

      let newData = { ...(blk.data || {}) };
      if (blk.data?.columns === 2) {
        const left = cloneIfChanged(blk.data.leftContent || []);
        const right = cloneIfChanged(blk.data.rightContent || []);
        if (!changed) return blk;
        newData.leftContent = left;
        newData.rightContent = right;
      } else {
        const content = (blk.data.content || []).map(col => cloneIfChanged(col || []));
        if (!changed) return blk;
        newData.content = content;
      }
      return { ...blk, data: newData };
    }

    return blk;
  };

  const updateBlock = (blockId, newData) => {
    const updatedBlocks = blocks.map((block) => deepUpdate(block, blockId, newData));

    setBlocks(updatedBlocks);

    if (selectedBlock && selectedBlock.id === blockId) {
      const updatedBlock = findBlockById(updatedBlocks, blockId);
      if (updatedBlock) {
        setSelectedBlock(updatedBlock);
      }
    }

    historyManager.current?.save({ blocks: updatedBlocks, settings: templateSettings });
    setHasUnsavedChanges(true);

    return { success: true, updatedBlocks };
  };

  const handleDeleteBlock = (blockId) => {
    const blockToDelete = findBlockById(blocks, blockId);

    if (blockToDelete?.type === 'unsubscribe' && !isAdminRole(user?.role)) {
      showNotification('Unsubscribe block cannot be deleted', 'error');
      return;
    }

    const newBlocks = blocks.filter((block) => block.id !== blockId);
    setBlocks(newBlocks);
    setSelectedBlock(null);
    historyManager.current.save({ blocks: newBlocks, settings: templateSettings });
    setHasUnsavedChanges(true);
  };

  const handleMoveBlock = (blockId, direction) => {
    const block = findBlockById(blocks, blockId);
    if (block?.type === 'unsubscribe') {
      showNotification('Unsubscribe block must stay at the bottom', 'warning');
      return;
    }

    const blockIndex = blocks.findIndex((b) => b.id === blockId);
    if (blockIndex === -1) return;

    const unsubscribeIndex = blocks.findIndex((b) => b.type === 'unsubscribe');
    const maxIndex = unsubscribeIndex === -1 ? blocks.length - 1 : unsubscribeIndex - 1;
    const targetIndex = direction === 'up' ? blockIndex - 1 : blockIndex + 1;

    if (targetIndex < 0 || targetIndex > maxIndex) return;

    const newBlocks = [...blocks];
    [newBlocks[blockIndex], newBlocks[targetIndex]] = [newBlocks[targetIndex], newBlocks[blockIndex]];
    const reordered = ensureUnsubscribeLast(newBlocks);
    setBlocks(reordered);
    historyManager.current.save({ blocks: reordered, settings: templateSettings });
    setHasUnsavedChanges(true);
  };

  const handleReorderBlocks = (newBlocks) => {
    const ordered = ensureUnsubscribeLast(newBlocks);
    setBlocks(ordered);
    historyManager.current.save({ blocks: ordered, settings: templateSettings });
    setHasUnsavedChanges(true);
  };

  const handleUndo = () => {
    const state = historyManager.current.undo();
    if (state) {
      setBlocks(state.blocks);
      setTemplateSettings(state.settings);
      setSelectedBlock(null);
      setHasUnsavedChanges(true);
    }
  };

  const handleRedo = () => {
    const state = historyManager.current.redo();
    if (state) {
      setBlocks(state.blocks);
      setTemplateSettings(state.settings);
      setSelectedBlock(null);
      setHasUnsavedChanges(true);
    }
  };

  const handleSave = async (shouldExit = false) => {
    if (!templateName.trim()) {
      showNotification('Please enter a template name', 'error');
      return;
    }

    setIsSaving(true);

    const templateData = {
      name: templateName,
      html: EmailExporterImproved.generateHTML(blocks, templateSettings), // Используем улучшенный экспортер
      css: EmailExporterImproved.generateCSS(blocks, templateSettings) || '', // Генерируем CSS
      blocks, // Сохраняем блоки для будущего редактирования
      settings: templateSettings,
    };
    
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { 'Authorization': `Bearer ${token}` } };

      let savedTemplateId = templateId;

      if (templateId) {
        // Обновляем существующий шаблон
        await axios.put(`/api/templates/${templateId}`, templateData, config);
        showNotification('Template updated successfully');
      } else {
        // Создаем новый шаблон
        const response = await axios.post('/api/templates', templateData, config);
        savedTemplateId = response.data._id;
        // Обновляем URL с новым ID без перезагрузки
        window.history.replaceState({}, '', `/templates/editor-pro/${savedTemplateId}`);
        showNotification('Template created successfully');
      }

      if (onSave) {
        await onSave(templateData);
      }
      
      setShowSaveDialog(false);
      setHasUnsavedChanges(false);
      
      // Если нужно выйти, переходим к списку шаблонов
      if (shouldExit) {
        navigate('/templates');
      }
    } catch (error) {
      console.error('Save error:', error);
      if (error.response?.status === 404) {
        showNotification('Template not found. Creating new template...', 'warning');
        // Если получили 404, попробуем создать новый шаблон
        try {
          const token = localStorage.getItem('token');
          const config = { headers: { 'Authorization': `Bearer ${token}` } };
          const response = await axios.post('/api/templates', templateData, config);
          const newTemplateId = response.data._id;
          window.history.replaceState({}, '', `/templates/editor-pro/${newTemplateId}`);
          showNotification('New template created successfully');
          setShowSaveDialog(false);
          setHasUnsavedChanges(false);
          if (shouldExit) {
            navigate('/templates');
          }
        } catch (createError) {
          console.error('Create error:', createError);
          showNotification('Error creating template', 'error');
        }
      } else {
        showNotification('Error saving template', 'error');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleExport = () => {
    const html = EmailExporterImproved.generateHTML(blocks, templateSettings);
    EmailExporterImproved.downloadHTML(html, templateName);
    showNotification('Template exported successfully');
  };

  const handleExit = () => {
    if (hasUnsavedChanges) {
      setShowExitConfirmDialog(true);
    } else {
      navigate('/templates');
    }
  };

  const handleConfirmExit = () => {
    setShowExitConfirmDialog(false);
    navigate('/templates');
  };

  const handleCancelExit = () => {
    setShowExitConfirmDialog(false);
  };

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: '#f5f5f5' }}>
      {/* Верхняя панель инструментов */}
      <AppBar position="static" sx={{ backgroundColor: '#2c3e50', boxShadow: 'none' }}>
        <Toolbar>
          <Tooltip title="Back to Templates">
            <IconButton 
              color="inherit" 
              onClick={handleExit}
              sx={{ mr: 2 }}
            >
              <ArrowBack />
            </IconButton>
          </Tooltip>
          
          <Typography variant="h6" sx={{ flexGrow: 1, color: '#ecf0f1' }}>
            Mailsball Editor Pro - {templateName}
          </Typography>
          
          {/* Кнопки действий */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Undo">
              <span>
                <IconButton 
                  color="inherit" 
                  onClick={handleUndo}
                  disabled={!canUndo}
                >
                  <Undo />
                </IconButton>
              </span>
            </Tooltip>
            
            <Tooltip title="Redo">
              <span>
                <IconButton 
                  color="inherit" 
                  onClick={handleRedo}
                  disabled={!canRedo}
                >
                  <Redo />
                </IconButton>
              </span>
            </Tooltip>

            <Divider orientation="vertical" flexItem sx={{ mx: 1, backgroundColor: '#34495e' }} />

            {/* Режимы просмотра */}
            <Tooltip title="Desktop View">
              <IconButton 
                color={previewMode === 'desktop' ? 'primary' : 'inherit'}
                onClick={() => setPreviewMode('desktop')}
              >
                <Computer />
              </IconButton>
            </Tooltip>
            
            <Tooltip title="Tablet View">
              <IconButton 
                color={previewMode === 'tablet' ? 'primary' : 'inherit'}
                onClick={() => setPreviewMode('tablet')}
              >
                <Tablet />
              </IconButton>
            </Tooltip>
            
            <Tooltip title="Mobile View">
              <IconButton 
                color={previewMode === 'mobile' ? 'primary' : 'inherit'}
                onClick={() => setPreviewMode('mobile')}
              >
                <Smartphone />
              </IconButton>
            </Tooltip>

            <Divider orientation="vertical" flexItem sx={{ mx: 1, backgroundColor: '#34495e' }} />

            <Tooltip title="Preview">
              <IconButton color="inherit" onClick={() => setShowPreview(true)}>
                <Preview />
              </IconButton>
            </Tooltip>

            <Tooltip title="Export HTML">
              <IconButton color="inherit" onClick={handleExport}>
                <Download />
              </IconButton>
            </Tooltip>

            <Tooltip title="Settings">
              <IconButton color="inherit" onClick={() => setShowSettingsDialog(true)}>
                <Settings />
              </IconButton>
            </Tooltip>

            <Button 
              color="inherit" 
              variant="outlined"
              onClick={() => templateName.trim() ? handleSave(false) : setShowSaveDialog(true)}
              sx={{ ml: 1, borderColor: '#ecf0f1', color: '#ecf0f1' }}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
            
            <Button 
              color="inherit" 
              variant="contained"
              onClick={() => setShowSaveDialog(true)}
              sx={{ ml: 1, backgroundColor: '#27ae60', '&:hover': { backgroundColor: '#2ecc71' } }}
              disabled={isSaving}
            >
              Save & Exit
            </Button>

            <Tooltip title="Close Editor">
              <IconButton color="inherit" onClick={handleExit}>
                <Close />
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Основная рабочая область */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Левая панель - Библиотека блоков */}
        <Paper 
          sx={{ 
            width: 280, 
            display: 'flex', 
            flexDirection: 'column',
            boxShadow: 'none',
            borderRadius: 0,
            borderRight: '1px solid #e0e0e0'
          }}
        >
          <BlockLibrary onAddBlock={handleAddBlock} />
        </Paper>

        {/* Центральная область - Холст */}
        <Box 
          sx={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column',
            backgroundColor: '#fff',
            overflow: 'hidden'
          }}
        >
          <Box 
            sx={{ 
              flex: 1, 
              overflow: 'auto',
              padding: 2,
              width: '100%'
            }}
          >
            <Box sx={{ width: '100%', maxWidth: responsiveWidths[previewMode], margin: '0 auto' }}>
              <EmailCanvas
                blocks={blocks}
                selectedBlock={selectedBlock}
                onSelectBlock={setSelectedBlock}
                onUpdateBlock={updateBlock}
                onDeleteBlock={handleDeleteBlock}
                onMoveBlock={handleMoveBlock}
                onAddBlock={handleAddBlock}
                onReorderBlocks={handleReorderBlocks}
                settings={templateSettings}
                previewMode={previewMode}
                user={user}
                showNotification={showNotification}
              />
            </Box>
          </Box>
        </Box>

        {/* Правая панель - Свойства */}
        <Paper 
          sx={{ 
            width: 320, 
            display: 'flex', 
            flexDirection: 'column',
            boxShadow: 'none',
            borderRadius: 0,
            borderLeft: '1px solid #e0e0e0'
          }}
        >
          <PropertyPanel
            selectedBlock={selectedBlock}
            templateSettings={templateSettings}
            user={user}
            onUpdateBlock={updateBlock}
            onUpdateSettings={handleUpdateSettings}
          />
        </Paper>
      </Box>

      {/* Диалог сохранения */}
      <Dialog open={showSaveDialog} onClose={() => setShowSaveDialog(false)}>
        <DialogTitle>Save Template</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Template Name"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            margin="normal"
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSaveDialog(false)}>Cancel</Button>
          <Button 
            onClick={() => handleSave(false)} 
            variant="outlined"
            disabled={isSaving || !templateName.trim()}
            sx={{ mr: 1 }}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
          <Button 
            onClick={() => handleSave(true)} 
            variant="contained"
            disabled={isSaving || !templateName.trim()}
          >
            {isSaving ? 'Saving...' : 'Save and Exit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог настроек */}
      <Dialog open={showSettingsDialog} onClose={() => setShowSettingsDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Template Settings</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Width (px)"
                type="number"
                value={templateSettings.width}
                onChange={(e) => handleUpdateSettings({
                  ...templateSettings,
                  width: parseInt(e.target.value) || 600
                })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Padding (px)"
                type="number"
                value={templateSettings.padding}
                onChange={(e) => handleUpdateSettings({
                  ...templateSettings,
                  padding: parseInt(e.target.value) || 20
                })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Background Color"
                value={templateSettings.backgroundColor}
                onChange={(e) => handleUpdateSettings({
                  ...templateSettings,
                  backgroundColor: e.target.value
                })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Font Family"
                value={templateSettings.fontFamily}
                onChange={(e) => handleUpdateSettings({
                  ...templateSettings,
                  fontFamily: e.target.value
                })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSettingsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Полноэкранный превью */}
      <PreviewPanel
        open={showPreview}
        onClose={() => setShowPreview(false)}
        blocks={blocks}
        settings={templateSettings}
        templateName={templateName}
      />

      {/* Диалог подтверждения выхода */}
      <Dialog open={showExitConfirmDialog} onClose={handleCancelExit}>
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <Typography>
            You have unsaved changes. Are you sure you want to leave without saving your template?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelExit}>Cancel</Button>
          <Button onClick={handleConfirmExit} color="error" variant="contained">
            Leave Without Saving
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading overlay */}
      <Backdrop open={isLoading} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Уведомления */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setNotification({ ...notification, open: false })} 
          severity={notification.severity}
          variant="filled"
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MailsballEditorPro; 